/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@ibm-watson/shared/store';
import { useChannelForm } from '../../hooks/useChannelForm';
import { selectSegmentCatalogItemId } from '../../store/catalog/selectors';
import { CATALOG_CHANNEL_TYPE } from '../../util/channelTypes';
import { ChannelProps } from '../../util/types';
import { TabbedHeader } from '../common/tabbed-header/TabbedHeader';
import { ConfigurationDetailsTab } from './configuration-details-tab/ConfigurationDetailsTab';
import { ConfigDetailsMessages as configMessages } from './configuration-details-tab/messages';
import { SegmentQuickSetup } from './new/SegmentQuickSetup';
import { settingsSchema } from './schema';

export const Segment = ({
  assistantId,
  environmentId,
  integrationId
}: ChannelProps) => {
  const catalogItemId = useAppSelector(selectSegmentCatalogItemId);

  const {
    loading,
    initialName,
    updateIntegrationName,
    formMethods,
    useQuickSetup,
    isReadOnly
  } = useChannelForm(
    CATALOG_CHANNEL_TYPE,
    assistantId,
    environmentId,
    integrationId,
    settingsSchema,
    catalogItemId,
    true
  );
  const { formatMessage: t } = useIntl();

  return (
    <FormProvider {...formMethods}>
      <TabbedHeader
        assistantId={assistantId}
        environmentId={environmentId}
        channelType={CATALOG_CHANNEL_TYPE}
        integrationName={initialName}
        updateIntegrationName={updateIntegrationName}
        useQuickSetup={useQuickSetup}
        loading={initialName.length === 0 || loading}
        QuickSetup={<SegmentQuickSetup />}
        Tabs={[
          <ConfigurationDetailsTab
            environmentId={environmentId}
            key="tab-1"
            tabname={t(configMessages.title)}
            readOnly={isReadOnly}
          />
        ]}
        readOnly={isReadOnly}
      />
    </FormProvider>
  );
};

export default Segment;
